/**
 * AppError — typed application error.
 *
 * Per CLAUDE.md ERROR 11: catch handlers must narrow `unknown` to a known
 * shape before reading properties. Throw `AppError` from anywhere that
 * produces a user-meaningful failure; catch with `isAppError(e)` and render
 * `e.userMessage`.
 */

export interface AppErrorInit {
  /** Stable, machine-readable code (e.g. 'INCOMPLETE_CHECKLIST'). */
  code:         string
  /** Developer-facing message (logs, audit trail). */
  message:      string
  /** User-facing message (rendered in UI). Must be safe to display. */
  userMessage:  string
  /** HTTP-style status hint for API responses (422, 403, etc.). */
  status?:      number
  /** Optional underlying cause. */
  cause?:       unknown
}

export class AppError extends Error {
  readonly code:         string
  readonly userMessage:  string
  readonly status:       number
  readonly underlying:   unknown

  constructor(init: AppErrorInit) {
    super(init.message)
    this.name        = 'AppError'
    this.code        = init.code
    this.userMessage = init.userMessage
    this.status      = init.status ?? 500
    this.underlying  = init.cause
  }
}

export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError
}
